import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsisVertical,
  faStar,
  faClock,
  faPrint,
} from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '../../utils/primitives';
import {
  type KdsOrderView,
  type KitchenStatusValue,
} from '../../../../services/kds/kds.service';
import { kdsI18n } from '../../i18n/kds-i18n';
import { type KdsTab, type KdsLine, stripOrderCode, formatElapsedTime, isLineCooking, isLineCancelled } from '../../utils/kds-page';
import { groupOrderlineSelections } from '../../../../utils/orderline-selections';
import classes from '../../styles/kds-page.module.scss';

const CHIP_COLORS = 12;
function chipColorIndex(label: string): number {
  let hash = 0;
  for (let i = 0; i < label.length; i += 1) {
    hash = (hash * 31 + label.charCodeAt(i)) >>> 0;
  }
  return (hash % CHIP_COLORS) + 1;
}

// Summary: Renders a single KDS order ticket matching the design reference.
export const KdsOrderCard: React.FC<{
  order: KdsOrderView;
  sourceOrder: KdsOrderView;
  orderTab: KdsTab;
  isPartial: boolean;
  isFavorite: boolean;
  onToggleFavorite: (orderId: number) => void;
  onStatusTap: (line: KdsLine, forcedNext?: KitchenStatusValue) => Promise<void>;
  onMarkAllReady: (order: KdsOrderView) => Promise<void>;
  onMarkAllCooking: (order: KdsOrderView) => Promise<void>;
  onMarkAllDone: (order: KdsOrderView) => Promise<void>;
  onMarkAllReadyFromDone: (order: KdsOrderView) => Promise<void>;
  onHide: (orderId: number, lines: KdsLine[], tab: KdsTab) => void;
  onPrint: (order: KdsOrderView, orderTab: KdsTab) => Promise<void>;
}> = ({ order, sourceOrder, orderTab, isPartial, isFavorite, onToggleFavorite, onStatusTap, onMarkAllReady, onMarkAllCooking, onMarkAllDone, onMarkAllReadyFromDone, onHide, onPrint }) => {
  const { t, i18n } = useTranslation(undefined, { i18n: kdsI18n });
  const [isReturnMenuOpen, setIsReturnMenuOpen] = useState(false);
  const [isPartialReturnMode, setIsPartialReturnMode] = useState(false);
  const returnMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isReturnMenuOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!returnMenuRef.current?.contains(event.target as Node)) {
        setIsReturnMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isReturnMenuOpen]);

  useEffect(() => {
    setIsPartialReturnMode(false);
  }, [orderTab]);

  const oldestLineTime =
    order.orderlines.length > 0
      ? order.orderlines.reduce(
          (min, l) => (l.createdAt < min ? l.createdAt : min),
          order.orderlines[0].createdAt,
        )
      : order.createdAt;

  return (
    <article
      className={[
        classes.card,
        isFavorite ? classes.cardPinned : '',
        order.orderType === 'take_away' ? classes.cardTakeAway : '',
      ].filter(Boolean).join(' ')}
      data-testid={`kds-card-${order.id}`}
    >

      <div className={[
        classes.cardHeader,
        orderTab === 'COOKING' ? classes.cardHeaderCooking
          : orderTab === 'READY' ? classes.cardHeaderReady
          : classes.cardHeaderDone,
        order.isVip ? classes.cardHeaderVip : '',
      ].filter(Boolean).join(' ')}>
        <div className={classes.cardHeaderTop}>
          
          <div className={classes.cardHeaderTopLeft}>
            {(orderTab === 'READY' || orderTab === 'DONE') && (
              <div className={classes.returnMenuWrap} ref={returnMenuRef}>
                <Button
                  className={classes.returnMenuBtn}
                  onClick={() => setIsReturnMenuOpen((prev) => !prev)}
                  aria-label={t('card.openReturnMenu')}
                  data-testid={`kds-card-${order.id}-return-menu`}
                >
                  <FontAwesomeIcon icon={faEllipsisVertical as IconProp} />
                </Button>
                {isReturnMenuOpen && (
                  <div className={classes.returnMenuPanel} dir={i18n.language === 'en' ? 'ltr' : 'rtl'}>
                    <Button
                      className={classes.returnMenuItem}
                      onClick={() => {
                        setIsPartialReturnMode((prev) => !prev);
                        setIsReturnMenuOpen(false);
                      }}
                      data-testid={`kds-card-${order.id}-edit`}
                    >
                      {isPartialReturnMode ? t('card.exitEdit') : t('card.edit')}
                    </Button>
                  </div>
                )}
              </div>
            )}
            <span className={classes.cardCodeBubble}>
              <Button
                className={[classes.pinBtn, isFavorite ? classes.pinBtnActive : ''].filter(Boolean).join(' ')}
                onClick={() => onToggleFavorite(order.id)}
                aria-label={isFavorite ? t('card.unpinOrder') : t('card.pinOrder')}
                data-testid={`kds-card-${order.id}-pin`}
              >
                <FontAwesomeIcon icon={faStar as IconProp} />
              </Button>
              <span className={classes.cardCodeChip}>
                {stripOrderCode(order.orderCode)}
              </span>
            </span>
          </div>

          
          <div className={classes.cardHeaderTopCenter}>
            <span className={classes.cardTimeChip}>
              <FontAwesomeIcon icon={faClock as IconProp} />
              &nbsp;{formatElapsedTime(oldestLineTime, i18n.language === 'en' ? 'en' : 'he')}
            </span>
          </div>

          
          <div className={classes.cardHeaderTopRight}>
            <span className={classes.cardTableNum}>{order.tableNumber}</span>
          </div>
        </div>

        
        <div className={classes.cardHeaderBottom}>
          <div className={classes.cardAreaNameWrap}>
            <span className={classes.cardAreaName}>{order.waiterName ?? t('card.waiterPlaceholder')}</span>
          </div>
          <div className={classes.cardChipsGroup}>
            {order.status === 'closed' && (
              <span className={classes.cardPaid}>{t('card.paid')}</span>
            )}
            {(orderTab === 'READY' || orderTab === 'DONE') && isPartial && (
              <span className={classes.partialReadyIndicator}>
                {orderTab === 'READY' ? t('card.partialReady') : t('card.partialDone')}
              </span>
            )}
            {order.orderType === 'take_away' ? (
              <span className={classes.cardTakeAwayTag}>{t('card.takeAway')}</span>
            ) : (
              <span className={classes.cardDineIn}>{order.areaName ?? t('card.dineIn')}</span>
            )}
          </div>
        </div>
      </div>

      
      <ul className={classes.linesList}>
        {(order.orderlines as KdsLine[]).map((line) => {
          const isCancelledLine = isLineCancelled(line);
          const isGreenLine = !isLineCooking(line);
          const isRowClickable =
            !isCancelledLine && (
              (orderTab === 'COOKING' && (isPartialReturnMode ? isGreenLine : !isGreenLine))
              || (orderTab === 'READY')
              || (orderTab === 'DONE' && isPartialReturnMode)
            );
          const forcedRowNextStatus: KitchenStatusValue | undefined =
            orderTab === 'COOKING'
              ? (isPartialReturnMode ? 'COOKING' : undefined)
              : orderTab === 'READY'
                ? (isPartialReturnMode ? 'COOKING' : 'DONE')
                : orderTab === 'DONE'
                  ? 'READY'
                  : undefined;

          return (
            <li
              key={`${line.lineKind}-${line.id}`}
              data-testid={`kds-order-line-row-${line.lineKind}-${line.id}`}
              className={[
                classes.lineItem,
                isRowClickable ? classes.lineItemClickable : '',
                orderTab === 'COOKING' && isGreenLine ? classes.lineItemReady : '',
                isCancelledLine ? classes.lineItemCancelled : '',
              ].filter(Boolean).join(' ')}
              onClick={isRowClickable ? (event) => {
                if (event.target instanceof HTMLElement && event.target.closest('button')) return;
                void onStatusTap(line, forcedRowNextStatus);
                if (isPartialReturnMode && (orderTab === 'READY' || orderTab === 'DONE')) {
                  setIsPartialReturnMode(false);
                }
              } : undefined}
            >
            {!isCancelledLine && (
              <Button
                className={[
                  classes.lineCheckbox,
                  isPartialReturnMode && isGreenLine ? classes.lineCheckboxBack : '',
                  !isPartialReturnMode && isGreenLine ? classes.lineCheckboxDone : '',
                ].filter(Boolean).join(' ')}
                onClick={(event) => {
                  event.stopPropagation();
                  if (orderTab === 'COOKING') {
                    if (isPartialReturnMode && isGreenLine) {
                      void onStatusTap(line, 'COOKING');
                      setIsPartialReturnMode(false);
                    } else if (!isPartialReturnMode && !isGreenLine) {
                      void onStatusTap(line);
                    }
                    return;
                  }
                  if (isPartialReturnMode && (orderTab === 'READY' || orderTab === 'DONE')) {
                    const partialBackTarget: KitchenStatusValue = orderTab === 'READY' ? 'COOKING' : 'READY';
                    void onStatusTap(line, partialBackTarget);
                    setIsPartialReturnMode(false);
                  }
                }}
                disabled={
                  (orderTab === 'COOKING' && !isPartialReturnMode && isGreenLine)
                  || (orderTab !== 'COOKING' && !isPartialReturnMode)
                }
                aria-label={t('card.toggleStatus', { productName: line.productName })}
                data-testid={`kds-line-${line.id}-checkbox`}
              >
                {isPartialReturnMode && isGreenLine
                  ? '↩︎'
                  : isGreenLine
                    ? '✔︎'
                    : ''}
              </Button>
            )}
            <span className={classes.lineName}>
              <span className={classes.lineProductNameRow}>
                <span className={classes.lineProductName}>{line.productName}</span>
              </span>
              {groupOrderlineSelections(line.selections).map((group, groupIndex) => {
                const text = `${group.groupName}: ${group.options.map((opt) => opt.name).join(', ')}`;
                return (
                  <span
                    key={`${group.groupName}-${groupIndex}`}
                    dir="auto"
                    className={[classes.chip, classes[`chip${chipColorIndex(group.groupName)}`]].join(' ')}
                  >
                    {text}
                  </span>
                );
              })}
              {line.wasReprinted && (
                <span className={classes.lineReprintTag} data-testid={`kds-line-${line.id}-reprint-tag`}>
                  {'שליחה חוזרת'}
                </span>
              )}
              {line.note && line.note.trim().length > 0 && (
                <span className={classes.lineNote} data-testid={`kds-line-${line.id}-note`}>
                  {line.note}
                </span>
              )}
            </span>
            <span className={classes.lineQty}>{line.quantity}</span>
            </li>
          );
        })}
      </ul>

      
      <div className={classes.cardFooter}>
        <Button className={classes.printBtn} aria-label={t('print.printTicket')} onClick={() => void onPrint(order, orderTab)} data-testid={`kds-card-${order.id}-print`}>
          <FontAwesomeIcon icon={faPrint as IconProp} />
        </Button>
        {orderTab === 'COOKING' && (
          <Button className={classes.readyBtn} onClick={() => void onMarkAllReady(order)} aria-label={t('card.markAllReady')} data-testid={`kds-card-${order.id}-ready`}>
            {t('card.buttonReady')}
          </Button>
        )}
        {orderTab === 'READY' && (
          <div className={classes.cardFooterActions}>
            {isPartialReturnMode && (
              <Button
                className={classes.backBtn}
                onClick={() => { void onMarkAllCooking(order); setIsPartialReturnMode(false); }}
                aria-label={t('card.markAllCooking')}
                data-testid={`kds-card-${order.id}-cooking`}
              >
                {t('card.buttonCooking')}
              </Button>
            )}
            <Button
              className={classes.doneBtn}
              onClick={() => void onMarkAllDone(order)}
              aria-label={t('card.markAllDone')}
              disabled={isPartialReturnMode}
              data-testid={`kds-card-${order.id}-done`}
            >
              {t('card.buttonDone')}
            </Button>
          </div>
        )}
        {orderTab === 'DONE' && (
          <div className={classes.cardFooterActions}>
            {isPartialReturnMode && (
              <Button
                className={classes.readyFromDoneBtn}
                onClick={() => { void onMarkAllReadyFromDone(order); setIsPartialReturnMode(false); }}
                aria-label={t('card.markAllReady')}
                data-testid={`kds-card-${order.id}-ready-from-done`}
              >
                {t('card.buttonReady')}
              </Button>
            )}
            <Button
              className={`${classes.hideBtn} btn-bordered`}
              onClick={() => onHide(order.id, order.orderlines as KdsLine[], orderTab)}
              aria-label={t('card.hideOrder')}
              disabled={isPartialReturnMode}
              data-testid={`kds-card-${order.id}-hide`}
            >
              {t('card.buttonHide')}
            </Button>
          </div>
        )}
      </div>
    </article>
  );
};
